/**
 * DataDashboard Component
 * Comprehensive data dashboard for Business tier and above users
 * Shows detailed history of all scanned items and results
 *
 * Requirements: Business tier feature
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { HistoryService } from '../services/HistoryService';
import { StatsService } from '../services/StatsService';
import type { UserStats } from '../services/StatsService';
import { SubscriptionService } from '../services/SubscriptionService';
import { AuthService } from '../services/AuthService';
import ResultsDisplay from '../components/ResultsDisplay';
import type { ClassificationResult } from '../types';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import './DataDashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function DataDashboard() {
  const [history, setHistory] = useState<ClassificationResult[]>([]);
  const [filteredHistory, setFilteredHistory] = useState<ClassificationResult[]>([]);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [, setHistoryFullyLoaded] = useState(false);
  const [statsData, setStatsData] = useState<UserStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [imageUrls, setImageUrls] = useState<{ [frameId: string]: string }>({});
  const [imageLoadingStates, setImageLoadingStates] = useState<{ [frameId: string]: 'loading' | 'loaded' | 'error' }>({});
  const [selectedItem, setSelectedItem] = useState<{item: ClassificationResult, baseFrameId: string} | null>(null);

  // View toggle state
  const [activeView, setActiveView] = useState<'history' | 'statistics'>('statistics');

  // Delete states
  const [deleteConfirmItem, setDeleteConfirmItem] = useState<ClassificationResult | null>(null);
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // all, today, week, month
  const [sortBy, setSortBy] = useState('newest'); // newest, oldest, price_high, price_low

  const [historyService] = useState(() => new HistoryService());
  const [statsService] = useState(() => new StatsService());
  const [subscriptionService] = useState(() => new SubscriptionService());
  const [authService] = useState(() => AuthService.getInstance());
  const [subscription, setSubscription] = useState<import('../types').Subscription | null>(null);

  // Use ref to track URLs for cleanup without causing re-renders
  const imageUrlsRef = useRef<{ [frameId: string]: string }>({});

  // Infinite-scroll pagination refs — using refs (not state) so the
  // loadNextPage callback never has a stale closure on these values.
  const historyOffsetRef = useRef(0);
  const isLoadingHistoryRef = useRef(false);
  const historyFullyLoadedRef = useRef(false);

  // Refs for the IntersectionObserver
  const sentinelRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const PAGE_SIZE = 10;

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [history, searchTerm, dateFilter, sortBy]);

  useEffect(() => {
    // Only cleanup when component unmounts - use ref to get current URLs
    return () => {
      Object.values(imageUrlsRef.current).forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, []);

  // Load images for visible items when history changes and we're in History view
  useEffect(() => {
    if (activeView === 'history' && filteredHistory.length > 0) {
      console.log('[DataDashboard] Loading images for History view...');
      loadImagesForVisibleItems();
    } else if (activeView === 'statistics') {
      console.log('[DataDashboard] In Statistics view - not loading images');
    }
  }, [filteredHistory, activeView]);

  const loadInitialData = async () => {
    setIsLoadingStats(true);
    setError(null);

    try {
      // Verify subscription access
      const subscriptionData = await subscriptionService.getSubscription();
      setSubscription(subscriptionData);
      const tier = subscriptionData.subscription_type.toLowerCase();
      const hasAccess = ['business', 'platinum', 'enterprise'].includes(tier);

      if (!hasAccess) {
        setError('Data Dashboard is available for Business tier and above. Please upgrade your plan to access this feature.');
        return;
      }

      // Load pre-aggregated stats — O(1) DynamoDB read (fast path) or lazy S3
      // backfill (first access). Either way, the Statistics view can render
      // as soon as this resolves.
      const stats = await statsService.getStats();
      setStatsData(stats);

    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load dashboard data');
    } finally {
      setIsLoadingStats(false);
    }
  };

  const loadNextPage = useCallback(async () => {
    if (isLoadingHistoryRef.current || historyFullyLoadedRef.current) return;

    isLoadingHistoryRef.current = true;
    setIsLoadingHistory(true);

    try {
      if (historyOffsetRef.current === 0) {
        historyService.clearCache();
      }

      const batch = await historyService.getHistory(PAGE_SIZE, historyOffsetRef.current);

      if (batch.length === 0) {
        historyFullyLoadedRef.current = true;
        setHistoryFullyLoaded(true);
        return;
      }

      historyOffsetRef.current += batch.length;

      setHistory(prev => {
        const existingIds = new Set(prev.map(item => item.frame_id));
        const newItems = batch.filter(item => !existingIds.has(item.frame_id));
        return [...prev, ...newItems];
      });

      // If fewer items than requested, we've reached the end
      if (batch.length < PAGE_SIZE) {
        historyFullyLoadedRef.current = true;
        setHistoryFullyLoaded(true);
      }

    } catch (err) {
      console.error('[DataDashboard] Failed to load history page:', err);
    } finally {
      isLoadingHistoryRef.current = false;
      setIsLoadingHistory(false);
    }
  }, [historyService]);

  // Trigger loadNextPage when the sentinel enters the scroll viewport
  useEffect(() => {
    if (activeView !== 'history') return;

    const sentinel = sentinelRef.current;
    const container = scrollContainerRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadNextPage();
        }
      },
      {
        root: container,
        rootMargin: '200px',
        threshold: 0,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [activeView, loadNextPage]);

  const handleTabChange = (view: 'statistics' | 'history') => {
    setActiveView(view);
    if (view === 'history' && !historyFullyLoadedRef.current && !isLoadingHistoryRef.current) {
      loadNextPage();
    }
  };

  const loadImagesForVisibleItems = async () => {
    // Group items by image and load images for the first 25 groups
    const groupedItems = groupItemsByImage(filteredHistory);
    const groupsToLoad = groupedItems.slice(0, 25);

    // Load images in batches of 5 to avoid overwhelming the server
    const batchSize = 5;
    for (let i = 0; i < groupsToLoad.length; i += batchSize) {
      const batch = groupsToLoad.slice(i, i + batchSize);

      // Load batch concurrently
      const loadPromises = batch.map(group => {
        const baseFrameId = group.baseFrameId;
        if (!imageUrls[baseFrameId] && imageLoadingStates[baseFrameId] !== 'loading') {
          return loadImageWithAuth(baseFrameId);
        }
        return Promise.resolve();
      });

      // Wait for batch to complete before starting next batch
      await Promise.allSettled(loadPromises);

      // Small delay between batches to be gentle on the server
      if (i + batchSize < groupsToLoad.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
  };

  const applyFilters = () => {
    let filtered = [...history];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.description?.toLowerCase().includes(term) ||
        item.criteria_results.some(cr =>
          cr.criterion_name.toLowerCase().includes(term) ||
          cr.selected_category.toLowerCase().includes(term)
        )
      );
    }

    // Apply date filter
    if (dateFilter !== 'all') {
      const now = new Date();
      const filterDate = new Date();

      switch (dateFilter) {
        case 'today':
          filterDate.setHours(0, 0, 0, 0);
          break;
        case 'week':
          filterDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          filterDate.setMonth(now.getMonth() - 1);
          break;
      }

      filtered = filtered.filter(item =>
        new Date(item.timestamp) >= filterDate
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'oldest':
          return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        case 'price_high':
          if (!subscription?.show_price) return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
          return (b.estimated_price || 0) - (a.estimated_price || 0);
        case 'price_low':
          if (!subscription?.show_price) return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
          return (a.estimated_price || 0) - (b.estimated_price || 0);
        case 'newest':
        default:
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      }
    });

    setFilteredHistory(filtered);
  };

  const groupItemsByImage = (items: ClassificationResult[]) => {
    const grouped = new Map<string, ClassificationResult[]>();

    items.forEach(item => {
      // Extract base frame_id (remove _item suffix if present)
      const baseFrameId = item.frame_id.includes('_item')
        ? item.frame_id.split('_item')[0]
        : item.frame_id;

      if (!grouped.has(baseFrameId)) {
        grouped.set(baseFrameId, []);
      }
      grouped.get(baseFrameId)!.push(item);
    });

    // Convert to array and sort each group by timestamp
    return Array.from(grouped.entries()).map(([baseFrameId, groupItems]) => {
      const sortedItems = groupItems.sort((a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );

      return {
        baseFrameId,
        items: sortedItems,
        // Use the most recent item's timestamp for sorting groups
        timestamp: sortedItems[0].timestamp,
        // Calculate total price for the group (only used when show_price is true)
        totalPrice: sortedItems.reduce((sum, item) => sum + (item.estimated_price || 0), 0)
      };
    }).sort((a, b) => {
      // Sort groups by the same criteria as individual items
      switch (sortBy) {
        case 'oldest':
          return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
        case 'price_high':
          if (!subscription?.show_price) return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
          return b.totalPrice - a.totalPrice;
        case 'price_low':
          if (!subscription?.show_price) return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
          return a.totalPrice - b.totalPrice;
        case 'newest':
        default:
          return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
      }
    });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStackedChartData = () => {
    // Build criteria map from pre-aggregated stats (O(1) — no history iteration)
    const criteriaStats = new Map<string, { [category: string]: number }>(
      Object.entries(statsData?.criteria_stats ?? {})
        .map(([k, v]) => [k, v as { [category: string]: number }])
    );

    const criteriaNames = Array.from(criteriaStats.keys());

    // Get all unique categories across all criteria
    const allCategories = new Set<string>();
    criteriaStats.forEach(categories => {
      Object.keys(categories).forEach(category => allCategories.add(category));
    });

    const categoryArray = Array.from(allCategories);

    // Create datasets for each category
    const datasets = categoryArray.map((category, index) => ({
      label: category,
      data: criteriaNames.map(criterionName => {
        const categories = criteriaStats.get(criterionName) || {};
        return categories[category] || 0;
      }),
      backgroundColor: [
        '#667eea',
        '#764ba2',
        '#f093fb',
        '#f5576c',
        '#4facfe',
        '#43e97b',
        '#fa709a',
        '#fee140',
        '#a8edea',
        '#fed6e3',
        '#c084fc',
        '#fb7185',
        '#34d399',
        '#fbbf24',
        '#60a5fa',
        '#f472b6',
        '#a78bfa',
        '#fb923c',
        '#4ade80',
        '#facc15',
      ][index % 20],
      borderColor: [
        '#5a67d8',
        '#6b46c1',
        '#ec4899',
        '#ef4444',
        '#3b82f6',
        '#10b981',
        '#f59e0b',
        '#eab308',
        '#06b6d4',
        '#f472b6',
        '#a855f7',
        '#e11d48',
        '#059669',
        '#d97706',
        '#2563eb',
        '#db2777',
        '#7c3aed',
        '#ea580c',
        '#16a34a',
        '#ca8a04',
      ][index % 20],
      borderWidth: 1,
    }));

    return {
      labels: criteriaNames,
      datasets,
      // Store criteria stats for tooltip access
      _criteriaStats: criteriaStats,
    };
  };

  const stackedChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hide the legend
      },
      title: {
        display: true,
        color: '#1f2937',
        font: {
          size: 16,
          weight: 600,
        },
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        filter: (tooltipItem: any) => {
          // Only show tooltip items that have a value > 0
          return tooltipItem.parsed.y > 0;
        },
        callbacks: {
          title: (context: any) => {
            return `${context[0].label}`;
          },
          label: (context: any) => {
            const value = context.parsed.y;
            if (value === 0) return undefined; // Don't show categories with 0 count
            return `${context.dataset.label}: ${value}`;
          },
          afterBody: (context: any) => {
            if (context.length === 0) return [];

            const criterionName = context[0].label;
            const chartData = getStackedChartData();
            const criteriaStats = chartData._criteriaStats;
            const categories = criteriaStats.get(criterionName) || {};
            const totalForCriterion = Object.values(categories).reduce((sum: number, count: number) => sum + count, 0);

            return [`Total for ${criterionName}: ${totalForCriterion}`];
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          color: '#6b7280',
          font: {
            size: 11,
          },
          maxRotation: 45,
        },
        grid: {
          color: '#e5e7eb',
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          color: '#6b7280',
          font: {
            size: 11,
          },
        },
        grid: {
          color: '#e5e7eb',
        },
      },
    },
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
  };

  const loadImageWithAuth = async (frameId: string): Promise<string> => {
    try {
      setImageLoadingStates(prev => ({ ...prev, [frameId]: 'loading' }));

      // Get the auth token using AuthService
      const token = await authService.getValidAccessToken();
      if (!token) {
        throw new Error('No auth token available');
      }

      // Fetch image with auth header
      const response = await fetch(`/api/images/${frameId}.jpg`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load image: ${response.status}`);
      }

      // Convert to blob URL
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      setImageUrls(prev => {
        const newUrls = { ...prev, [frameId]: blobUrl };
        imageUrlsRef.current = newUrls; // Keep ref in sync
        return newUrls;
      });
      setImageLoadingStates(prev => ({ ...prev, [frameId]: 'loaded' }));

      return blobUrl;
    } catch (error) {
      console.error(`Failed to load image for frame ${frameId}:`, error);
      setImageLoadingStates(prev => ({ ...prev, [frameId]: 'error' }));

      // Return placeholder
      const placeholderUrl = getPlaceholderImage();
      setImageUrls(prev => {
        const newUrls = { ...prev, [frameId]: placeholderUrl };
        imageUrlsRef.current = newUrls; // Keep ref in sync
        return newUrls;
      });
      return placeholderUrl;
    }
  };

  const handleSaveFeedback = useCallback(async (frameId: string, feedback: string) => {
    await historyService.saveFeedback(frameId, feedback);
    setHistory((prev) =>
      prev.map((item) => item.frame_id === frameId ? { ...item, user_feedback: feedback } : item)
    );
  }, [historyService]);

  const handleItemClick = (group: { baseFrameId: string; items: ClassificationResult[] }) => {
    // Show the first item in detail view, but include all items for context
    setSelectedItem({item: group.items[0], baseFrameId: group.baseFrameId});

    // NO IMAGE LOADING - only use already loaded images
    console.log('Modal opened for baseFrameId:', group.baseFrameId);
    console.log('Image available:', !!imageUrls[group.baseFrameId]);
  };

  const handleDeleteClick = (group: { baseFrameId: string; items: ClassificationResult[] }, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    // Set the first item for the delete confirmation, but we'll delete the entire group
    setDeleteConfirmItem(group.items[0]);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmItem) return;

    setIsDeleting(true);
    try {
      // Extract base frame_id to delete entire group
      const baseFrameId = deleteConfirmItem.frame_id.includes('_item')
        ? deleteConfirmItem.frame_id.split('_item')[0]
        : deleteConfirmItem.frame_id;

      // Delete using base frame_id to remove entire group
      await historyService.deleteHistoryItem(baseFrameId);

      // Remove all items with the same base frame_id from local state
      setHistory(prev => prev.filter(item => {
        const itemBaseFrameId = item.frame_id.includes('_item')
          ? item.frame_id.split('_item')[0]
          : item.frame_id;
        return itemBaseFrameId !== baseFrameId;
      }));

      // Clean up image URL if it exists
      if (imageUrls[baseFrameId]) {
        URL.revokeObjectURL(imageUrls[baseFrameId]);
        setImageUrls(prev => {
          const newUrls = { ...prev };
          delete newUrls[baseFrameId];
          imageUrlsRef.current = newUrls; // Keep ref in sync
          return newUrls;
        });
      }

      setDeleteConfirmItem(null);
    } catch (error) {
      console.error('Failed to delete item group:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete item group');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteAllClick = () => {
    setShowDeleteAllConfirm(true);
  };

  const handleDeleteAllConfirm = async () => {
    setIsDeleting(true);
    try {
      await historyService.deleteAllHistory();

      // Clear all local state
      setHistory([]);
      setFilteredHistory([]);

      // Reset pagination so the list can be re-loaded after delete-all
      historyOffsetRef.current = 0;
      historyFullyLoadedRef.current = false;
      isLoadingHistoryRef.current = false;
      setHistoryFullyLoaded(false);

      // Clean up all image URLs
      Object.values(imageUrls).forEach(url => {
        if (url.startsWith('blob:')) {
          URL.revokeObjectURL(url);
        }
      });
      setImageUrls({});
      imageUrlsRef.current = {}; // Keep ref in sync
      setImageLoadingStates({});

      setShowDeleteAllConfirm(false);
    } catch (error) {
      console.error('Failed to delete all items:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete all items');
    } finally {
      setIsDeleting(false);
    }
  };

  const getPlaceholderImage = (): string => {
    // SVG placeholder for failed image loads - shows a textile/clothing icon
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDMyMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxnIHRyYW5zZm9ybT0idHJhbnNsYXRlKDEzNSA3NSkiPgo8cGF0aCBkPSJNMjUgMEgyNUMzOC44MDcxIDAgNTAgMTEuMTkyOSA1MCAyNVYyNUM1MCAzOC44MDcxIDM4LjgwNzEgNTAgMjUgNTBIMjVDMTEuMTkyOSA1MCAwIDM4LjgwNzEgMCAyNVYyNUMwIDExLjE5MjkgMTEuMTkyOSAwIDI1IDBaIiBmaWxsPSIjRDFENURCIi8+CjxwYXRoIGQ9Ik0xNSAxNUgzNVYzNUgxNVYxNVoiIGZpbGw9IiNBN0I2QzIiLz4KPHN2ZyB4PSIyMCIgeT0iMjAiIHdpZHRoPSIxMCIgaGVpZ2h0PSIxMCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSIjNkI3MjgwIj4KPHA+VGV4dGlsZTwvcD4KPC9zdmc+CjwvZz4KPHR4dCB4PSIxNjAiIHk9IjE0NSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzZCNzI4MCIgZm9udC1mYW1pbHk9InNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTIiIGZvbnQtd2VpZ2h0PSI1MDAiPkltYWdlIE5vdCBBdmFpbGFibGU8L3R4dD4KPC9zdmc+';
  };

  // Derive stats from pre-aggregated data (no history iteration required)
  const stats = {
    totalItems: statsData?.total_scans ?? 0,
    totalValue: statsData?.total_value ?? 0,
    avgValue: statsData && statsData.total_scans > 0
      ? statsData.total_value / statsData.total_scans
      : 0,
  };

  if (error) {
    return (
      <div className="data-dashboard">
        <div className="dashboard-container">
          <div className="dashboard-header">
            <div className="error-message" role="alert">
              <span className="error-icon">⚠️</span>
              <span>{error}</span>
              {error.includes('upgrade') && (
                <button
                  className="upgrade-btn"
                  onClick={() => window.location.href = '/account'}
                >
                  Upgrade Plan
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="data-dashboard">
      <div className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-content">
            <div className="header-left">
              <h1>Data Dashboard</h1>
              <div className="view-toggle">
                <button
                  className={`toggle-btn ${activeView === 'statistics' ? 'active' : ''}`}
                  onClick={() => handleTabChange('statistics')}
                >
                  Statistics
                </button>
                <button
                  className={`toggle-btn ${activeView === 'history' ? 'active' : ''}`}
                  onClick={() => handleTabChange('history')}
                >
                  History
                </button>
              </div>
            </div>
            <div className="dashboard-stats">
              {isLoadingStats ? (
                <>
                  <span className="stat-badge skeleton-badge"></span>
                  <span className="stat-badge skeleton-badge"></span>
                  <span className="stat-badge skeleton-badge"></span>
                </>
              ) : (
                <>
                  <span className="stat-badge total">{stats.totalItems} Items</span>
                  {subscription?.show_price && (
                    <>
                      <span className="stat-badge value">€{stats.totalValue.toFixed(0)} Total</span>
                      <span className="stat-badge average">€{stats.avgValue.toFixed(0)} Avg</span>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Dashboard Content */}
        <div className="dashboard-content" ref={scrollContainerRef}>
          {activeView === 'statistics' ? (
            /* Statistics View */
            <div className="statistics-view">
              {isLoadingStats ? (
                <div className="statistics-content">
                  <div className="chart-section">
                    <div className="chart-container">
                      <h3>Category Distribution by Criteria</h3>
                      <div className="chart-wrapper skeleton-chart"></div>
                    </div>
                  </div>
                </div>
              ) : !statsData || statsData.total_scans === 0 ? (
                <div className="no-data">
                  <div className="no-data-icon">📊</div>
                  <h3>No data available</h3>
                  <p>Start classifying items to see statistics</p>
                </div>
              ) : (
                <div className="statistics-content">
                  {/* Stacked Bar Chart */}
                  <div className="chart-section">
                    <div className="chart-container">
                      <h3>Category Distribution by Criteria</h3>
                      <div className="chart-wrapper">
                        <Bar
                          data={getStackedChartData()}
                          options={{
                            ...stackedChartOptions,
                            plugins: {
                              ...stackedChartOptions.plugins,
                              title: {
                                ...stackedChartOptions.plugins.title,
                                display: false, // Remove the chart title
                              },
                            },
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* History View */
            <div className="history-view">
              <div className="content-header">
                <div className="compact-filters">
                  <div className="filter-group">
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="filter-input-compact"
                    />
                  </div>
                  <div className="filter-group">
                    <select
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                      className="filter-select-compact"
                    >
                      <option value="all">All Time</option>
                      <option value="today">Today</option>
                      <option value="week">Week</option>
                      <option value="month">Month</option>
                    </select>
                  </div>
                  <div className="filter-group">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="filter-select-compact"
                    >
                      <option value="newest">Newest</option>
                      <option value="oldest">Oldest</option>
                      {subscription?.show_price && (
                        <>
                          <option value="price_high">High €</option>
                          <option value="price_low">Low €</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>
              </div>

              {/* Skeleton cards while first batch is loading */}
              {isLoadingHistory && filteredHistory.length === 0 ? (
                <div className="history-grid">
                  {[...Array(4)].map((_, i) => (
                    <div key={i} className="history-item-card skeleton-card">
                      <div className="history-item-image skeleton-image"></div>
                      <div className="history-item-content">
                        <div className="skeleton-line skeleton-title-line"></div>
                        <div className="skeleton-line skeleton-date-line"></div>
                        <div className="skeleton-line skeleton-criteria-line"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredHistory.length === 0 ? (
                <div className="no-results">
                  <div className="no-results-icon">🔍</div>
                  <h3>No items found</h3>
                  <p>Try adjusting your search or filter criteria</p>
                </div>
              ) : (
                <>
                  <div className="history-grid">
                    {groupItemsByImage(filteredHistory).map((group, index) => (
                      <div
                        key={group.baseFrameId || index}
                        className="history-item-card"
                        onClick={() => handleItemClick(group)}
                      >
                        <div className="history-item-image">
                          {imageLoadingStates[group.baseFrameId] === 'loading' ? (
                            <div className="image-loading-container">
                              <div className="loading-spinner"></div>
                              <div className="loading-text">Loading...</div>
                            </div>
                          ) : imageUrls[group.baseFrameId] ? (
                            <img
                              src={imageUrls[group.baseFrameId]}
                              alt={group.items[0].description || 'Classification result'}
                              onError={(e) => {
                                const imgElement = e.target as HTMLImageElement;
                                imgElement.src = getPlaceholderImage();
                                setImageLoadingStates(prev => ({ ...prev, [group.baseFrameId]: 'error' }));
                              }}
                            />
                          ) : (
                            <div className="image-placeholder">
                              <div className="placeholder-icon">📷</div>
                              <div className="placeholder-text">Loading image...</div>
                            </div>
                          )}
                        </div>
                        <div className="history-item-content">
                          <div className="history-item-header">
                            <h3 className="history-item-title">
                              {group.items.length > 1
                                ? `${group.items.length} Classifications`
                                : (group.items[0].description || 'Classification Result')
                              }
                            </h3>
                            <div className="history-item-actions">
                              <div className="history-item-date">
                                {formatDate(group.items[0].timestamp)}
                              </div>
                              <button
                                className="delete-btn"
                                onClick={(e) => handleDeleteClick(group, e)}
                                title="Delete this entire group"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                          <div className="history-item-details">
                            {subscription?.show_price && group.totalPrice > 0 && (
                              <div className="history-item-price">
                                €{group.totalPrice.toFixed(2)}
                                {group.items.length > 1 && (
                                  <span className="price-breakdown"> ({group.items.length} items)</span>
                                )}
                              </div>
                            )}
                            <div className="history-item-classifications">
                              {group.items.map((item, itemIndex) => (
                                <div key={itemIndex} className="classification-item">
                                  <div className="classification-header">
                                    {item.description && (
                                      <span className="classification-title">{item.description}</span>
                                    )}
                                    {subscription?.show_price && item.estimated_price && (
                                      <span className="classification-price">€{item.estimated_price.toFixed(2)}</span>
                                    )}
                                  </div>
                                  <div className="classification-criteria">
                                    {item.criteria_results.slice(0, 3).map((cr, idx) => (
                                      <span key={idx} className="criteria-tag">
                                        {cr.criterion_name}: {cr.selected_category}
                                      </span>
                                    ))}
                                    {item.criteria_results.length > 3 && (
                                      <span className="criteria-more">
                                        +{item.criteria_results.length - 3} more
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Progressive load indicator */}
                  {isLoadingHistory && (
                    <div className="loading-more-row">
                      <div className="loading-more-spinner"></div>
                      Loading more...
                    </div>
                  )}
                </>
              )}

              {/* Delete All Button */}
              {filteredHistory.length > 0 && (
                <div className="delete-all-section">
                  <button
                    className="delete-all-btn"
                    onClick={handleDeleteAllClick}
                    disabled={isDeleting}
                  >
                    {isDeleting ? 'Deleting...' : 'Delete All Items'}
                  </button>
                  <p className="delete-all-warning">
                    ⚠️ This will permanently delete all your classification history
                  </p>
                </div>
              )}

              {/* Sentinel — IntersectionObserver fires loadNextPage when visible */}
              <div ref={sentinelRef} style={{ height: 1 }} />
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirmItem && (
          <div className="delete-modal" onClick={() => setDeleteConfirmItem(null)}>
            <div className="delete-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="delete-modal-header">
                <h3>Delete Classification Group</h3>
                <button
                  className="delete-modal-close"
                  onClick={() => setDeleteConfirmItem(null)}
                >
                  ×
                </button>
              </div>
              <div className="delete-modal-body">
                <p>Are you sure you want to delete this entire classification group?</p>
                <p className="delete-item-info">
                  <strong>
                    {(() => {
                      const baseFrameId = deleteConfirmItem.frame_id.includes('_item')
                        ? deleteConfirmItem.frame_id.split('_item')[0]
                        : deleteConfirmItem.frame_id;
                      const groupItems = history.filter(item => {
                        const itemBaseFrameId = item.frame_id.includes('_item')
                          ? item.frame_id.split('_item')[0]
                          : item.frame_id;
                        return itemBaseFrameId === baseFrameId;
                      });
                      return groupItems.length > 1
                        ? `${groupItems.length} Classifications`
                        : (deleteConfirmItem.description || 'Classification Result');
                    })()}
                  </strong>
                  <br />
                  <small>{formatDate(deleteConfirmItem.timestamp)}</small>
                </p>
                <p className="delete-warning">This will delete the image and all associated classification results. This action cannot be undone.</p>
              </div>
              <div className="delete-modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setDeleteConfirmItem(null)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  className="confirm-delete-btn"
                  onClick={handleDeleteConfirm}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Group'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete All Confirmation Modal */}
        {showDeleteAllConfirm && (
          <div className="delete-modal" onClick={() => setShowDeleteAllConfirm(false)}>
            <div className="delete-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="delete-modal-header">
                <h3>Delete All Classifications</h3>
                <button
                  className="delete-modal-close"
                  onClick={() => setShowDeleteAllConfirm(false)}
                >
                  ×
                </button>
              </div>
              <div className="delete-modal-body">
                <p>Are you sure you want to delete <strong>ALL</strong> your classification history?</p>
                <p className="delete-count">
                  This will permanently delete <strong>{history.length} items</strong> and their associated images.
                </p>
                <p className="delete-warning">⚠️ This action cannot be undone!</p>
              </div>
              <div className="delete-modal-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setShowDeleteAllConfirm(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  className="confirm-delete-all-btn"
                  onClick={handleDeleteAllConfirm}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting All...' : 'Delete All'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Detail Modal */}
        {selectedItem && (
          <div className="detail-modal" onClick={() => setSelectedItem(null)}>
            <div className="detail-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="detail-modal-header">
                <h2>Classification Details</h2>
                <button
                  className="detail-modal-close"
                  onClick={() => setSelectedItem(null)}
                >
                  ×
                </button>
              </div>
              <div className="detail-modal-body">
                <div className="detail-modal-image">
                  {(() => {
                    // Use the baseFrameId that was stored when the modal was opened
                    const baseFrameId = selectedItem.baseFrameId;

                    // Check if image is already loaded (it should be from the main dashboard)
                    if (imageUrls[baseFrameId]) {
                      return (
                        <img
                          src={imageUrls[baseFrameId]}
                          alt={selectedItem.item.description || 'Classification result'}
                          onError={(e) => {
                            const imgElement = e.target as HTMLImageElement;
                            imgElement.src = getPlaceholderImage();
                          }}
                        />
                      );
                    } else {
                      // Fallback - this should rarely happen since image should already be loaded
                      return (
                        <div className="image-placeholder-large">
                          <div className="placeholder-icon">📷</div>
                          <div className="placeholder-text">Image not available</div>
                        </div>
                      );
                    }
                  })()}
                </div>
                <div className="detail-modal-results">
                  {(() => {
                    // Use the stored baseFrameId to find all related items
                    const baseFrameId = selectedItem.baseFrameId;
                    const groupItems = history.filter(item => {
                      const itemBaseFrameId = item.frame_id.includes('_item')
                        ? item.frame_id.split('_item')[0]
                        : item.frame_id;
                      return itemBaseFrameId === baseFrameId;
                    });

                    return groupItems.map((item, index) => (
                      <div key={index} className="detail-classification-item">
                        <div className="classification-item-header">
                          <h4>Item {index + 1}</h4>
                          {subscription?.show_price && item.estimated_price && (
                            <span className="classification-item-price">€{item.estimated_price.toFixed(2)}</span>
                          )}
                        </div>
                        {item.description && (
                          <p className="classification-item-description">{item.description}</p>
                        )}
                        <div className="classification-item-results">
                          <ResultsDisplay result={item} variant="modal" onSaveFeedback={handleSaveFeedback} subscription={subscription} />
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DataDashboard;
